interface IAccordionOptions {
  allowMultiple: boolean;
  toggleSelector: string;
}

class Accordion {
  static DEFAULTS: IAccordionOptions;

  element: HTMLElement;
  toggleAll: HTMLElement;
  settings: IAccordionOptions;
  toggles: HTMLElement[];
  openCount: number;

  constructor(element: HTMLElement, options?: IAccordionOptions) {
    this.element = element;
    this.settings = { ...Accordion.DEFAULTS, ...options };
    this.toggles = null;
    this.toggleAll = null;
    this.openCount = 0;

    this.init();
  }

  init() {
    this.toggles = Array.from(
      this.element.querySelectorAll(this.settings.toggleSelector),
    );
    this.toggleAll = this.element.querySelector(".js-accordion__toggle-all");

    const open = this.toggles.filter((toggle) => this.isOpen(toggle));
    open.forEach((toggle) => this.open(toggle));

    this.bindHandlers();
  }

  bindHandlers() {
    this.toggles.forEach((toggle) => {
      toggle.addEventListener("click", this.handleToggleClick);
      toggle.addEventListener("keydown", this.handleToggleKeyDown);
    });

    if (this.toggleAll) {
      this.toggleAll.addEventListener("click", this.handleToggleAllClick);
    }
  }

  handleToggleClick = (e) => {
    const toggle = e.currentTarget;

    if (!this.isOpen(toggle)) {
      this.open(toggle);
    } else {
      this.close(toggle);
    }
  };

  handleToggleAllClick = (e) => {
    const isExpanded = this.toggleAll.getAttribute("aria-expanded");

    if (isExpanded === "false") {
      this.openAll();
    } else {
      this.closeAll();
    }
  };

  handleToggleKeyDown = (e) => {
    const target = e.target;
    const key = e.which.toString();
    const toggles = this.toggles;

    // Up/ Down arrow and Control + Page Up/ Page Down keyboard operations
    // 38 = Up, 40 = Down
    if (key.match(/38|40/) || (e.ctrlKey && key.match(/33|34/))) {
      e.preventDefault();

      const index = toggles.indexOf(target);
      const direction = key.match(/34|40/) ? 1 : -1;
      const length = toggles.length;
      const newIndex = (index + length + direction) % length;

      toggles[newIndex].focus();
    }

    if (key.match(/35|36/)) {
      e.preventDefault();

      // 35 = End, 36 = Home keyboard operations
      switch (key) {
        // Go to first accordion item
        case "36":
          toggles[0].focus();
          break;
        // Go to last accordion item
        case "35":
          toggles[toggles.length - 1].focus();
          break;
      }
    }
  };

  closeAll() {
    this.toggles.forEach((toggle) => this.close(toggle));
    this.toggleAll.setAttribute("aria-expanded", "false");
  }

  openAll() {
    this.toggles.forEach((toggle) => this.open(toggle));
    this.toggleAll.setAttribute("aria-expanded", "true");
  }

  open(toggle) {
    const panelId = toggle.getAttribute("aria-controls");
    const panel: HTMLElement = this.element.querySelector(`#${panelId}`);

    toggle.setAttribute("aria-expanded", "true");
    panel.style.maxHeight = panel.scrollHeight + "px";

    this.setText(toggle, false);
    this.updateOpenCount();
  }

  close(toggle) {
    const panelId = toggle.getAttribute("aria-controls");
    const panel: HTMLElement = this.element.querySelector(`#${panelId}`);

    toggle.setAttribute("aria-expanded", "false");
    panel.style.maxHeight = null;

    this.setText(toggle, true);
    this.updateOpenCount();
  }

  setText(toggle, isShow) {
    if (!toggle) {
      return;
    }
    const textElement = toggle.querySelector("[data-text]");
    const text = JSON.parse(textElement.dataset.text);

    textElement.innerText = isShow ? text.show : text.hide;
  }

  updateOpenCount() {
    const openCount = (this.openCount = this.toggles.filter((toggle) =>
      this.isOpen(toggle),
    ).length);

    const allOpen = openCount === this.toggles.length;

    if (!this.toggleAll) {
      return;
    }
    this.setText(this.toggleAll, !allOpen);
    this.toggleAll.setAttribute("aria-expanded", allOpen ? "true" : "false");
  }

  isOpen(toggle) {
    return toggle.getAttribute("aria-expanded") === "true";
  }
}

Accordion.DEFAULTS = {
  allowMultiple: true,
  toggleSelector: ".js-accordion__toggle",
};

export default Accordion;
